import React, { useEffect, useState, useCallback, useRef } from 'react';
import { bool, func, number, object, string, shape } from 'prop-types';
import classnames from 'classnames';
import { Autocomplete, Button, ButtonGroup, Select, Slideout, Radio, RadioGroup } from '../../../../../ps-aspect-design';
import SettingsCard from './SettingsCard';
import './SettingsSlideout.scss';
import debounce from 'lodash/debounce';
import { toCompareWithLocationValue, toPayScaleLocationDisplay } from '../helpers';

SettingSlideout.propTypes = {
  alertType: string,
  saveTrendSettings: func,
  setAppliedGeoDiffs: func,
  setShowSettingsSlideout: func,
  settings: shape({
    compareWithLocation: string,
    groupByJobFamily: bool,
    internalData: string,
    showGrouped: bool,
    trendRange: number,
    trendRangeType: string
  }),
  showApplyGeo: bool,
  showOnlySearch: bool,
  showInternalDataSetting: bool,
  showSettingsSlideout: bool,
  supportedLocations: object,
  searchPayScaleLocations: func
};

const trendRangeTypeItems = [
  { key: 'quarters', text: 'Quarters' },
  { key: 'years', text: 'Years' }
];

const internalDataOptions = [
  { key: 'cityState', text: 'City/State' },
  { key: 'workLocation', text: 'Work Location' }
];

const quarterRangeOptions = [
  { key: 1, text: 1 },
  { key: 2, text: 2 },
  { key: 3, text: 3 },
  { key: 4, text: 4 },
  { key: 5, text: 5 },
  { key: 6, text: 6 },
  { key: 7, text: 7 },
  { key: 8, text: 8 }
];

const yearRangeOptions = [
  { key: 1, text: 1 },
  { key: 2, text: 2 }
];

function SettingSlideout(props) {
  const {
    alertType,
    saveTrendSettings,
    setAppliedGeoDiffs,
    setShowSettingsSlideout,
    settings,
    showApplyGeo,
    showOnlySearch,
    showInternalDataSetting,
    showSettingsSlideout,
    searchPayScaleLocations,
    supportedLocations
  } = props;
  const [trendRange, setTrendRange] = useState();
  const [internalData, setInternalData] = useState();
  const [trendRangeType, setTrendRangeType] = useState();
  const [groupByJobFamily, setGroupByJobFamily] = useState();
  const [showGrouped, setShowGrouped] = useState();
  const [compareWithLocation, setCompareWithLocation] = useState();
  const [compareWithInputValue, setCompareWithInputValue] = useState('');
  const [payScaleLocationResult, setPayScaleLocationResult] = useState();
  const [isLoadingResult, setIsLoadingResult] = useState(false);

  useEffect(() => {
    if (Object.keys(settings).length) {
      const internalDataValue = internalDataOptions.find(option => option.key === settings.internalData)?.text;
      setTrendRange(settings.trendRange);
      setTrendRangeType(settings.trendRangeType);
      setGroupByJobFamily(settings.groupByJobFamily);
      setShowGrouped(settings.showGrouped);
      setInternalData(internalDataValue);
      const compareWithLocation = toPayScaleLocationDisplay(settings.compareWithLocation) || 'National';
      setCompareWithLocation(compareWithLocation);
    }
  }, [settings]);

  useEffect(() => {
    if (!showSettingsSlideout) {
      resetPreferences();
    }
  }, [showSettingsSlideout]);

  const debouncedPayScaleLocations = useCallback(debounce(getLocationMappingSearchResults, 750), [
    compareWithLocation,
    supportedLocations
  ]);

  function getLocationMappingSearchResults(params) {
    setIsLoadingResult(true);
    return searchPayScaleLocations(supportedLocations, params).then(results => {
      const mappedResults = results.map(result => result.label);
      setPayScaleLocationResult(mappedResults);
      setIsLoadingResult(false);
      return mappedResults;
    });
  }

  function handleTrendRangeTypeChange(type) {
    if (type === 'Years' && trendRange > 2) {
      setTrendRange(1);
    }

    setTrendRangeType(type);
  }

  function resetPreferences() {
    const internalDataValue = internalDataOptions.find(option => option.key === settings.internalData)?.text;
    setTrendRange(settings.trendRange);
    setTrendRangeType(settings.trendRangeType);
    setGroupByJobFamily(settings.groupByJobFamily);
    setShowGrouped(settings.showGrouped);
    setInternalData(internalDataValue);
    const compareWithLocation = toPayScaleLocationDisplay(settings.compareWithLocation) || 'National';
    setCompareWithLocation(compareWithLocation);
    setCompareWithInputValue('');
    setShowSettingsSlideout(false);
  }

  function savePreferences() {
    const internalDataValue = internalDataOptions.find(option => option.text === internalData)?.key;
    const newSettings = {
      trendRange: parseInt(trendRange),
      trendRangeType,
      groupByJobFamily,
      showGrouped,
      internalData: internalDataValue,
      compareWithLocation: toCompareWithLocationValue(compareWithLocation)
    };
    setAppliedGeoDiffs([]);
    saveTrendSettings(newSettings).then(() => setShowSettingsSlideout(false));
  }

  const renderJobFamilySetting = (
    <>
      <span>Group by</span>
      <RadioGroup
        onSelectionChange={() => {
          setGroupByJobFamily(!groupByJobFamily);
        }}
        selectedValue={groupByJobFamily ? 'Location' : 'Job Family'}
      >
        <Radio label="Location" value="Location" />
        <Radio label="Job Family" value="Job Family" />
      </RadioGroup>
    </>
  );

  const renderRangeSetting = (
    <>
      <span>Last</span>
      <Select
        aria-label="Trend Range Select"
        className={classnames('range-select', { 'years-select': trendRangeType === 'Years' })}
        componentRef={useRef(null)}
        defaultValue={trendRange}
        handleChange={setTrendRange}
        options={trendRangeType === 'Quarters' ? quarterRangeOptions : yearRangeOptions}
        value={trendRange}
      />
      <Select
        aria-label="Trend Range Type Select"
        className="trend-select"
        componentRef={useRef(null)}
        defaultValue={trendRangeType}
        handleChange={handleTrendRangeTypeChange}
        options={trendRangeTypeItems}
        value={trendRangeType}
      />
    </>
  );

  const renderInternalDataSetting = (
    <Select
      aria-label="Internal Data Type Select"
      className="internal-data-select"
      componentRef={useRef(null)}
      defaultValue={internalData}
      handleChange={setInternalData}
      options={internalDataOptions}
      value={internalData}
    />
  );

  const renderCompareWith = (
    <Autocomplete
      value={compareWithInputValue}
      isLoading={isLoadingResult}
      suggestions={payScaleLocationResult}
      forceSelect={true}
      placeholder={compareWithLocation}
      onInputChange={value => {
        setCompareWithInputValue(value);
        return debouncedPayScaleLocations({
          params: {
            workLocation: value
          }
        });
      }}
      onSelectionChange={value => {
        setCompareWithInputValue('');
        setCompareWithLocation(value);
      }}
    />
  );

  const renderHeader = <h2>Data Settings</h2>;

  const renderFooter = (
    <ButtonGroup>
      <Button className="ps-slideout__button--cancel" onClick={() => resetPreferences()} variant="secondary">
        Cancel
      </Button>
      <Button onClick={() => savePreferences()} variant="primary">
        Save
      </Button>
    </ButtonGroup>
  );

  return (
    <>
      <Slideout
        className="data-settings-slideout"
        finalFocusRef={useRef(null)}
        footer={renderFooter}
        header={renderHeader}
        isOpen={showSettingsSlideout}
        onClose={setShowSettingsSlideout}
        showOverlay
        size="small"
      >
        <div className="data-settings-slideout__content">
          {showInternalDataSetting && (
            <SettingsCard
              containerClass={classnames({ 'setting-alert': alertType === 'failure' })}
              id="internalData"
              setting={renderInternalDataSetting}
              title="Internal data for geo differential location matching"
            />
          )}
          {(showApplyGeo || showOnlySearch) && (
            <SettingsCard id="compare-with" setting={renderCompareWith} title="Compare with" />
          )}
          <SettingsCard id="range" setting={renderRangeSetting} title="Date range for geo trend" />
          <SettingsCard
            className="group-setting"
            customSettingToggle={setShowGrouped}
            disabled={showApplyGeo}
            id="family"
            setting={renderJobFamilySetting}
            title="Show geo differential by job family"
            toggle={showApplyGeo ? false : showGrouped}
          />
        </div>
      </Slideout>
    </>
  );
}

export default SettingSlideout;
